import type {ReactNode, KeyboardEvent} from 'react'
import {useEffect, useRef, useState} from 'react'
import {faCheck} from '@fortawesome/free-solid-svg-icons'
import {faTimes} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import isNil from 'lodash/isNil'
import noop from 'lodash/noop'
import {exhaustive} from '@freckle/exhaustive'
import {
  addMaybeClassName,
  addIfClassNames,
} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  checkboxLabel,
  fakeCheckbox,
  keyboardFocus,
  regularFakeCheckbox,
  regularLargeCheckbox,
  regularSelectedCheckbox,
  regularIncorrectCheckbox,
  regularDisabledCheckbox,
  regularLabelText,
  regularDisabledLabel,
  truncatedLabel,
  showAllLink,
} from './tri-state-regular-checkbox.module.scss'

type CheckboxState = 'selected' | 'incorrect' | 'unselected' | 'disabled'
type SizeT = 'small' | 'large'

export type Props<T> = {
  id: string
  label: ReactNode | string
  value: T
  state: CheckboxState
  size?: SizeT
  addLabelClass?: string
  addInputClass?: string
  onSelected: (value: T) => void
  dataTest?: string
  showTwoLinesWithLinkToExpand?: boolean
}

function TriStateCheckbox<T>(props: Props<T>) {
  const {
    id,
    label,
    value,
    state,
    onSelected,
    addLabelClass,
    addInputClass,
    size = 'small',
    dataTest,
    showTwoLinesWithLinkToExpand = false,
  } = props
  const {t} = useSafeTranslation('common')
  const labelledId = `${id}-label`
  const checkboxId = `${id}-checkbox`
  const [shouldOutline, setShouldOutline] = useState(true)
  const [expanded, setExpanded] = useState(false)
  const [showExpandLink, setShowExpandLink] = useState(false)
  const onClick =
    state === 'disabled'
      ? noop
      : () => {
          onSelected(value)
          setShouldOutline(false)
        }

  const onKeyDown =
    state === 'disabled'
      ? noop
      : (e: KeyboardEvent<EventTarget>) => pressSpace(e, () => onSelected(value))
  const onFocusChange = () => setShouldOutline(true)
  const handleExpandClick = () => setExpanded(!expanded)
  const labelRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (showTwoLinesWithLinkToExpand && labelRef.current) {
      const elementHeight = labelRef.current.offsetHeight
      const lineHeight = parseFloat(window.getComputedStyle(labelRef.current).lineHeight)

      if (elementHeight > lineHeight * 1.5) {
        setShowExpandLink(true)
      } else {
        setShowExpandLink(false)
      }
    }
  }, [label, expanded, showTwoLinesWithLinkToExpand])

  const truncatedLabelStyle = addIfClassNames([
    [showTwoLinesWithLinkToExpand && showExpandLink && !expanded, truncatedLabel],
  ])

  return (
    <>
      <label
        id={labelledId}
        className={addMaybeClassName(checkboxLabel, addLabelClass)}
        onClick={onClick}
        htmlFor={checkboxId}
      >
        <span
          role="checkbox"
          aria-checked={state === 'selected'}
          aria-labelledby={labelledId}
          aria-disabled={state === 'disabled'}
          className={getCheckboxClassName(state, size, addInputClass, shouldOutline)}
          tabIndex={0}
          id={checkboxId}
          onBlur={onFocusChange}
          onFocus={onFocusChange}
          onKeyDown={onKeyDown}
          data-test={dataTest}
        >
          {getIcon(state)}
        </span>
        <span ref={labelRef} className={truncatedLabelStyle}>
          {renderLabel(state, label)}
          {showTwoLinesWithLinkToExpand && showExpandLink && !expanded ? (
            <Button
              style="link"
              className={showAllLink}
              onClick={e => {
                e.stopPropagation()
                handleExpandClick()
              }}
            >
              {t('SHOW_ALL')}
            </Button>
          ) : null}
        </span>
      </label>
    </>
  )
}

function renderLabel(state: CheckboxState, label: ReactNode): ReactNode {
  if (typeof label === 'string') {
    const regularLabelTextClass = addIfClassNames([
      [true, regularLabelText],
      [state === 'disabled', regularDisabledLabel],
    ])
    return (
      <Text style="body-2" className={regularLabelTextClass}>
        {label}
      </Text>
    )
  } else {
    return label
  }
}

const SPACE = 32

function pressSpace(event: KeyboardEvent<EventTarget>, f: () => void) {
  switch (event.keyCode) {
    case SPACE:
      f()
      event.stopPropagation()
      event.preventDefault()
      break

    default:
      break
  }
}

function getIcon(state: CheckboxState): ReactNode | undefined | null {
  switch (state) {
    case 'selected':
      return <FontAwesomeIcon icon={faCheck} />
    case 'incorrect':
      return <FontAwesomeIcon icon={faTimes} />
    case 'unselected':
    case 'disabled':
      return null
    default:
      return exhaustive(state, `Invalid checkbox state ${state}`)
  }
}

function getCheckboxClassName(
  state: CheckboxState,
  size: SizeT,
  addInputClass: string | undefined | null,
  shouldOutline: boolean,
): string {
  const baseClassName = addIfClassNames([
    [true, fakeCheckbox],
    [true, regularFakeCheckbox],
    [size === 'large', regularLargeCheckbox],
    [!isNil(addInputClass), addInputClass],
    [shouldOutline, keyboardFocus],
  ])

  switch (state) {
    case 'selected':
      return [baseClassName, regularSelectedCheckbox].join(' ')
    case 'unselected':
      return baseClassName
    case 'incorrect':
      return [baseClassName, regularIncorrectCheckbox].join(' ')
    case 'disabled':
      return [baseClassName, regularDisabledCheckbox].join(' ')
    default:
      return exhaustive(state, `Invalid checkbox state ${state}`)
  }
}

export {TriStateCheckbox}
